import type { Editor } from "@tiptap/core"
import { useEditorState } from "@tiptap/react"

/**
 * The mark's active color across the selection, as a rendered CSS color:
 * - a color string if the mark is active with ONE uniform color everywhere
 * - `null` if the mark is absent (its default / unset state)
 * - `undefined` if the color varies across the selection (no single active color)
 *
 * Shared by the text-color and highlight toolbar controls, whose "is a color active"
 * question is identical apart from the mark name. Underline is deliberately NOT built on
 * this: it has a default-color active state (an underline with no explicit color), so its
 * selector treats `null` as active rather than as the unset state.
 */
export function useActiveMarkColor(editor: Editor, markName: string): string | null | undefined {
  return useEditorState({
    editor,
    selector: (snapshot) => {
      const color = snapshot.editor.getAttributes(markName)?.color as string | undefined
      if (color === undefined) return null
      return snapshot.editor.isActive(markName, { color }) ? color : undefined
    },
  })
}
